<script lang="ts" setup>
import helpers from '~/helpers'
import type { BrandProductReview } from '~/types/reviews'

const props = defineProps<{
  reviewData: BrandProductReview
}>()

const showDetailsComment = ref(false)

const authorName = computed(() => props.reviewData.meta.authorName)
const comment = computed(() => props.reviewData.attributes.comment)
const isAnonymous = computed(() => props.reviewData.attributes.isAnonymous)
const score = computed(() => props.reviewData.attributes.score)
const productData = computed(() => props.reviewData.relationships.product.data)
const productName = computed(() => productData.value.attributes.name)
const reply = computed(() => props.reviewData.attributes.reply)
</script>

<template>
  <div class="brand-review-card bg-white">
    <div class="brand-review-card__wrapper flex flex-col justify-between">
      <div class="brand-review-card__row">
        <div class="brand-review-card__info">
          <div class="brand-review-card__score mb-1">
            <Score :score="score" />
          </div>
          <div class="brand-review-card__name mb-1">
            {{ helpers.getReviewAuthorName(authorName, isAnonymous) }}
          </div>
        </div>
        <div v-if="comment !== null" class="brand-review-card__body">
          <p class="brand-review-card__comment pre-wrap">
            {{ !showDetailsComment && comment.length > 300 ? `${comment.slice(0, 300)}...` : comment }}
          </p>
          <BlockWithBorder v-if="reply && showDetailsComment" class="brand-review-card__reply mt-4">
            <div class="brand-review-card__company text-main-green">
              iTAB
              <SvgIcon name="outlined/check-circle" />
            </div>
            <pre class="pre-wrap">{{ reply }}</pre>
          </BlockWithBorder>
          <a
            v-if="(comment.length > 300 || reply) && !showDetailsComment"
            class="brand-review-card__link whitespace-nowrap"
            @click="showDetailsComment = !showDetailsComment"
          >
            Читать полностью
          </a>
        </div>
      </div>
      <div class="brand-review-card__row">
        <NuxtLink
          class="brand-review-card__link whitespace-nowrap"
          :title="productName"
          :to="helpers.getProductPath(productData)"
        >
          {{ productName }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.brand-review-card {
  border-radius: 12px;
  width: 100%;

  &__body {
    height: 200px;
    overflow-y: auto;
    padding-right: 10px;
  }

  &__comment,
  &__reply {
    text-align: start;

    @media (max-width: 580px) {
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
  }

  &__company {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 1rem;

    & > svg {
      margin-left: 5px;
    }
  }

  &__info {
    margin-bottom: 24px;

    @media (max-width: 580px) {
      margin-bottom: 16px;
    }
  }

  &__link {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 580px) {
      font-size: 14px;
    }
  }

  &__name {
    color: variables.$main-gray-color;
    font-size: 22px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.5em;

    @media (max-width: 580px) {
      font-size: 16px;
    }
  }

  &__score {
    font-size: 24px;
  }

  &__wrapper {
    gap: 1rem;
    height: calc(100% - 24px * 2);
    padding: 24px;

    @media (max-width: 580px) {
      font-size: 18px;
    }
  }
}
</style>
